import React, { useEffect, useState } from "react";
import { Star, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { reviewsApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EVENT_TYPES = ["Mariage", "Anniversaire", "Entreprise", "Photobooth", "Baptême", "Communion", "Association", "Senior", "Autre"];

function StarsInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="review-stars-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`review-star-${n}`}
          className="text-[#DCB457] hover:scale-110 transition"
          aria-label={`Note ${n} sur 5`}
        >
          <Star size={28} strokeWidth={1.3} fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
      <span className="ml-3 text-sm text-white/55">{value}/5</span>
    </div>
  );
}

export default function LaisserAvis() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    author_name: "",
    city: "",
    event_type: "Mariage",
    event_date: "",
    rating: 5,
    title: "",
    content: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [myReviews, setMyReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        author_name: f.author_name || [user.prenom, user.nom].filter(Boolean).join(" ") || user.email || "",
      }));
    }
  }, [user]);

  const load = () => {
    reviewsApi.myList()
      .then(setMyReviews)
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.content.trim().length < 20) {
      toast.error("Votre avis doit faire au moins 20 caractères.");
      return;
    }
    setSubmitting(true);
    try {
      await reviewsApi.mySubmit(form);
      toast.success("Merci ! Votre avis sera publié après modération.");
      setForm({ ...form, title: "", content: "", rating: 5 });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-laisser-avis" className="space-y-8">
      <header>
        <p className="eyebrow">Votre expérience</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Laissez un <span className="italic gold-text">avis.</span></h1>
        <p className="text-white/60 text-sm mt-3 max-w-2xl">
          Votre retour nous aide à progresser et à rassurer les futurs clients. Chaque avis est lu et modéré sous 48h.
        </p>
      </header>

      <form onSubmit={submit} className="card-luxe p-6 md:p-8 space-y-5" data-testid="review-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Prénom (et initiale du nom)">
            <input className="input-luxe" required value={form.author_name} onChange={(e) => update("author_name", e.target.value)} data-testid="review-author" />
          </Field>
          <Field label="Ville de l'événement">
            <input className="input-luxe" placeholder="Lille, Tourcoing, Arras…" value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="review-city" />
          </Field>
          <Field label="Type d'événement">
            <select className="input-luxe" value={form.event_type} onChange={(e) => update("event_type", e.target.value)} data-testid="review-event-type">
              {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Date de l'événement">
            <input type="date" className="input-luxe" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} data-testid="review-event-date" />
          </Field>
        </div>

        <Field label="Note">
          <StarsInput value={form.rating} onChange={(n) => update("rating", n)} />
        </Field>

        <Field label="Titre court (optionnel)">
          <input className="input-luxe" placeholder="Soirée magique, ambiance dingue !" value={form.title} onChange={(e) => update("title", e.target.value)} maxLength={120} data-testid="review-title" />
        </Field>

        <Field label="Votre avis *">
          <textarea
            rows={6}
            className="input-luxe resize-none"
            placeholder="Comment s'est passée la prestation ? Qu'avez-vous particulièrement apprécié ? (20 caractères minimum)"
            value={form.content}
            onChange={(e) => update("content", e.target.value)}
            data-testid="review-content"
          />
          <p className="text-[11px] text-white/40 mt-1">{form.content.length} / 2000</p>
        </Field>

        <button type="submit" disabled={submitting} data-testid="review-submit" className="btn-gold w-full md:w-auto inline-flex items-center gap-2 disabled:opacity-60">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={1.4} />}
          Envoyer mon avis
        </button>
      </form>

      {/* Mes avis */}
      <section data-testid="my-reviews-section">
        <h2 className="font-serif-luxe text-2xl mt-10">Mes avis soumis.</h2>
        {loading ? (
          <p className="text-white/55 italic mt-4">Chargement…</p>
        ) : myReviews.length === 0 ? (
          <p className="text-white/55 italic mt-4">Vous n'avez pas encore soumis d'avis.</p>
        ) : (
          <div className="space-y-3 mt-4">
            {myReviews.map((r) => (
              <div key={r.id} className="card-luxe p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-0.5 text-[#DCB457]">
                      {[1,2,3,4,5].map((n) => <Star key={n} size={12} strokeWidth={1.3} fill={n <= r.rating ? "currentColor" : "none"} />)}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/55 border border-white/15 px-2 py-0.5">{r.event_type}</span>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                {r.title && <h3 className="font-serif-luxe text-lg mt-3">{r.title}</h3>}
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{r.content}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "published")
    return <span data-testid="review-status-published" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1"><CheckCircle2 size={11} strokeWidth={1.5} /> Publié</span>;
  if (status === "rejected")
    return <span data-testid="review-status-rejected" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-red-300 border border-red-400/30 px-2 py-1">Refusé</span>;
  return <span data-testid="review-status-pending" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-white/55 border border-white/15 px-2 py-1"><Clock size={11} strokeWidth={1.5} /> En attente</span>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
